import { useRoute, useLocation } from "wouter";
import { useState } from "react";
import { format } from "date-fns";
import {
  useGetLead, getGetLeadQueryKey,
  useUpdateLead,
  LeadStage, LeadDetail
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Phone, Calendar, MapPin, Building, Clock, Edit2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STAGES = Object.values(LeadStage);

export default function LeadDetailView() {
  const [, params] = useRoute("/leads/:id");
  const [, setLocation] = useLocation();
  const leadId = params?.id ? parseInt(params.id) : 0;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: lead, isLoading } = useGetLead(leadId, {
    query: { enabled: !!leadId, queryKey: getGetLeadQueryKey(leadId) }
  });

  const updateLead = useUpdateLead();

  const handleStageChange = (newStage: LeadStage) => {
    updateLead.mutate({ id: leadId, data: { stage: newStage } }, {
      onSuccess: () => {
        toast({ title: "Stage Updated", description: `Lead moved to ${newStage.replace(/_/g, " ")}` });
        queryClient.setQueryData(getGetLeadQueryKey(leadId), (old: LeadDetail | undefined) =>
          old ? { ...old, stage: newStage } : old
        );
      },
      onError: () => toast({ variant: "destructive", title: "Error", description: "Failed to update stage" })
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-1/3" />
        <div className="grid gap-6 md:grid-cols-3">
          <Card className="md:col-span-2"><CardContent className="p-6"><Skeleton className="h-64 w-full" /></CardContent></Card>
          <Card><CardContent className="p-6"><Skeleton className="h-64 w-full" /></CardContent></Card>
        </div>
      </div>
    );
  }

  if (!lead) return <div>Lead not found</div>;

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">{lead.customerName}</h1>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setLocation(`/leads/${leadId}/edit`)}
              className="h-8 w-8 text-muted-foreground"
              aria-label="Edit lead"
            >
              <Edit2 className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-muted-foreground flex items-center mt-1">
            <Phone className="w-4 h-4 mr-2" /> {lead.mobileNumber}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium">Stage:</span>
          <Select value={lead.stage} onValueChange={(v) => handleStageChange(v as LeadStage)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STAGES.map(s => (
                <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {lead.stage === "closed_won" && (
            <Button onClick={() => setLocation(`/accounts/convert/${lead.id}`)}>
              Convert to Member
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Requirement Details</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-y-4">
              <div>
                <p className="text-sm text-muted-foreground">Space Type</p>
                <p className="font-medium capitalize flex items-center">
                  <Building className="w-4 h-4 mr-2 text-muted-foreground" />
                  {lead.spaceType.replace(/_/g, " ")}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Seats Required</p>
                <p className="font-medium">{lead.seatRangeMin} - {lead.seatRangeMax} seats</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Location Target</p>
                <div className="font-medium flex items-center mt-1">
                  <MapPin className="w-4 h-4 mr-2 text-muted-foreground" />
                  <span>{lead.space.name} ({lead.location})</span>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Urgency</p>
                <Badge variant="outline" className="capitalize mt-1">{lead.urgency}</Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Source</p>
                <p className="font-medium capitalize">{lead.source.replace(/_/g, " ")}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Priority</p>
                <Badge variant="outline" className="capitalize mt-1">
                  {lead.priority} ({lead.priorityScore}/100)
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Lead Status</p>
                <Badge
                  variant="outline"
                  className={`capitalize mt-1 ${
                    lead.leadStatus === "qualified" ? "border-transparent bg-green-100 text-green-700" :
                    lead.leadStatus === "disqualified" ? "border-transparent bg-red-100 text-red-700" :
                    lead.leadStatus === "converted" ? "border-transparent bg-blue-100 text-blue-700" :
                    ""
                  }`}
                >
                  {lead.leadStatus}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Nature of Business</p>
                <p className="font-medium">{lead.natureOfBusiness || "—"}</p>
              </div>
              {lead.objectionType && (
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Objection</p>
                  <p className="font-medium capitalize">
                    {lead.objectionType}
                    {lead.objectionType === "location" && lead.expectedLocation
                      ? ` — expected: ${lead.expectedLocation}`
                      : ""}
                    {lead.objectionType === "price" && lead.expectedPrice
                      ? ` — ideal: ${lead.expectedPrice}`
                      : ""}
                    {lead.objectionType === "other" && lead.objectionReason
                      ? ` — ${lead.objectionReason}`
                      : ""}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground">Call Status</p>
                <Badge variant="outline" className="capitalize mt-1">
                  {lead.callStatus.replace(/_/g, " ")}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Number Verified</p>
                <p className="font-medium">{lead.numberVerified ? "Yes" : "No"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Campaign ID</p>
                <p className="font-medium">{lead.campaignId || "—"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Follow-ups</p>
                <p className="font-medium">{lead.followUpCount}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Activity Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              {lead.activity.length === 0 ? (
                <p className="text-sm text-muted-foreground">No activity recorded yet.</p>
              ) : (
                <div className="space-y-4">
                  {lead.activity.map((act, index) => (
                    <div key={act.id} className="relative pl-6 pb-4">
                      {index !== lead.activity.length - 1 && (
                        <div className="absolute left-[11px] top-6 bottom-0 w-px bg-border"></div>
                      )}
                      <div className="absolute left-1 top-1.5 w-5 h-5 rounded-full border-2 border-primary bg-background z-10 flex items-center justify-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      </div>
                      <p className="text-sm font-medium">{act.action}</p>
                      {act.details && <p className="text-sm text-muted-foreground mt-1">{act.details}</p>}
                      <div className="flex items-center mt-2 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3 mr-1" />
                        {format(new Date(act.createdAt), "MMM d, yyyy h:mm a")} by {act.actor}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Scheduling</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">Next Follow Up</p>
                <p className="font-medium flex items-center mt-1">
                  <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
                  {lead.nextFollowUpDate ? format(new Date(lead.nextFollowUpDate), "MMM d, yyyy") : "Not scheduled"}
                </p>
              </div>
              <Separator />
              <div>
                <p className="text-sm text-muted-foreground">Callback Scheduled</p>
                <p className="font-medium mt-1">
                  {lead.callbackScheduledDate ? format(new Date(lead.callbackScheduledDate), "MMM d, yyyy") : "None"}
                </p>
                {lead.callbackScheduledSlot && (
                  <p className="text-sm text-muted-foreground">{lead.callbackScheduledSlot}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

    </div>
  );
}
